import type { UserData, Drink, ApiResponse, BackupListResponse, PasswordVerifyResult, RecoveryResult } from '@/types'

const API_BASE = '/api'

/**
 * Error thrown when the server responds with a non-2xx status.
 * Distinct from network errors (TypeError from fetch) so callers can
 * tell "server said no" from "couldn't reach server".
 */
export class ApiHttpError extends Error {
  constructor(public status: number, message: string) {
    super(message)
    this.name = 'ApiHttpError'
  }
}

/** True when the thrown error is a fetch network failure (offline, DNS, CORS). */
export function isNetworkError(err: unknown): boolean {
  return err instanceof TypeError
}

// Helper to check response and parse JSON
async function parseResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const body = await response.json().catch(() => ({}))
    const msg = (body as { error?: string }).error || `API error: ${response.status}`
    throw new ApiHttpError(response.status, msg)
  }
  return response.json()
}

// GET user data from KV
export async function fetchUserData(userId: string): Promise<ApiResponse<{ key: string; value: UserData } | null>> {
  const response = await fetch(`${API_BASE}/storage/${encodeURIComponent(userId)}`)
  return parseResponse(response)
}

// PUT user data to KV
export async function saveUserData(userId: string, data: UserData): Promise<ApiResponse> {
  const response = await fetch(`${API_BASE}/storage/${encodeURIComponent(userId)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ value: JSON.stringify(data) }),
  })
  return parseResponse(response)
}

// List available backups
export async function listBackups(userId: string): Promise<BackupListResponse> {
  const response = await fetch(`${API_BASE}/storage/${encodeURIComponent(userId)}?backups=list`)
  return parseResponse(response)
}

// Get specific backup
export async function getBackup(userId: string, timestamp: string): Promise<ApiResponse<{ key: string; value: UserData; isBackup: boolean }>> {
  const response = await fetch(`${API_BASE}/storage/${encodeURIComponent(userId)}?backups=${timestamp}`)
  return parseResponse(response)
}

// Set password
export async function setPassword(
  userId: string,
  password: string,
  currentPassword?: string,
  passwordMode: 'on_load' | 'after_timeout' = 'on_load',
  timeoutMinutes?: number
): Promise<ApiResponse> {
  const response = await fetch(`${API_BASE}/user/${encodeURIComponent(userId)}/password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ password, currentPassword, passwordMode, timeoutMinutes }),
  })
  return parseResponse(response)
}

// Verify password
export async function verifyPassword(userId: string, password: string): Promise<PasswordVerifyResult> {
  const response = await fetch(`${API_BASE}/user/${encodeURIComponent(userId)}/password/verify`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ password }),
  })
  return parseResponse(response)
}

// Remove password
export async function removePassword(userId: string, currentPassword: string): Promise<ApiResponse> {
  const response = await fetch(`${API_BASE}/user/${encodeURIComponent(userId)}/password`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ currentPassword }),
  })
  return parseResponse(response)
}

// Update settings (backup email, password mode, timeout)
export async function updateSettings(
  userId: string,
  settings: {
    backupEmail?: string
    passwordMode?: 'on_load' | 'after_timeout'
    timeoutMinutes?: number
    currentPassword?: string
  }
): Promise<ApiResponse> {
  const response = await fetch(`${API_BASE}/user/${encodeURIComponent(userId)}/settings`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(settings),
  })
  return parseResponse(response)
}

// AI drink lookup
export async function lookupDrink(query: string): Promise<ApiResponse<Drink>> {
  const response = await fetch(`${API_BASE}/lookup/drink`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ query }),
  })
  return parseResponse(response)
}

// DELETE user data from KV
export async function deleteUserData(userId: string): Promise<ApiResponse> {
  const response = await fetch(`${API_BASE}/storage/${encodeURIComponent(userId)}`, {
    method: 'DELETE',
  })
  return parseResponse(response)
}

// Send recovery email
export async function sendRecoveryEmail(email: string): Promise<RecoveryResult> {
  const response = await fetch(`${API_BASE}/recover`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  })
  return parseResponse(response)
}

// Check if user has password protection
export async function checkPasswordProtection(userId: string): Promise<{ hasPassword: boolean; passwordMode?: 'on_load' | 'after_timeout'; timeoutMinutes?: number }> {
  const result = await fetchUserData(userId)
  if (result.success && result.data?.value) {
    const userData = result.data.value
    return {
      hasPassword: !!(userData.passwordHash && userData.passwordSalt),
      passwordMode: userData.passwordMode,
      timeoutMinutes: userData.timeoutMinutes,
    }
  }
  return { hasPassword: false }
}
